<template>
  <nav
    aria-label="Breadcrumb"
    :class="[
      'breadcrumbs-container',
      variant === 'compact' ? 'text-xs' : ''
    ]"
  >
    <ol class="flex items-center flex-wrap min-w-0 gap-y-0.5">
      <li
        v-for="(item, index) in items"
        :key="breadcrumbKey(item, index)"
        class="flex items-center min-w-0"
        :class="{ 'breadcrumb-current': variant === 'compact' && isCurrentItem(item, index) }"
      >
        <component
          :is="isCurrentItem(item, index) || !item.to ? 'span' : 'router-link'"
          :to="item.to"
          :class="itemClasses(item, index)"
          :aria-current="isCurrentItem(item, index) ? 'page' : undefined"
        >
          <BaseIcon v-if="item.icon" size="small" class="mr-1 flex-shrink-0">
            <component :is="item.icon" />
          </BaseIcon>
          <span :class="variant === 'emphasized' ? 'truncate block min-w-0' : ''">{{ item.label }}</span>
        </component>

        <span
          v-if="index < items.length - 1"
          :class="separatorWrapperClass"
          aria-hidden="true"
        >
          <i
            v-if="separatorVariant === 'chevron'"
            :class="chevronIconClass"
            aria-hidden="true"
          />
          <template v-else>{{ separator }}</template>
        </span>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { BreadcrumbItem } from '@/types/breadcrumbs'
import BaseIcon from '@/components/ui/BaseIcon.vue'

interface Props {
  /** Array of breadcrumb items to display */
  items: BreadcrumbItem[]
  /** Separator character when separatorVariant is slash */
  separator?: string
  /** Slash text vs chevron icon between items */
  separatorVariant?: 'slash' | 'chevron'
  /** compact = page-header strip (text-xs); emphasized = top bar (larger) */
  variant?: 'compact' | 'emphasized'
}

const props = withDefaults(defineProps<Props>(), {
  separator: '/',
  separatorVariant: 'slash',
  variant: 'compact'
})

const isCurrentItem = (item: BreadcrumbItem, index: number): boolean => {
  return item.isCurrent === true || index === props.items.length - 1
}

/** Stable list keys when the trail shortens (prefix crumbs stay patched in place). */
function breadcrumbKey(item: BreadcrumbItem, index: number): string {
  const label = item.label ?? ''
  const toPart =
    item.to === undefined || item.to === null
      ? ''
      : typeof item.to === 'string'
        ? item.to
        : JSON.stringify(item.to)
  return `${index}-${label}-${toPart}`
}

const separatorWrapperClass =
  'breadcrumb-separator flex items-center justify-center flex-shrink-0 text-text-muted select-none'

/** Chevron size: match compact (text-xs) trail in top bar; compact strip uses same */
const chevronIconClass = computed(() =>
  props.variant === 'emphasized'
    ? 'ri-arrow-right-s-line text-xs sm:text-sm leading-none text-text-muted/80 flex-shrink-0 transition-colors duration-150 ease-out'
    : 'ri-arrow-right-s-line text-base leading-none text-text-muted flex-shrink-0'
)

function itemClasses(item: BreadcrumbItem, index: number): string[] {
  const current = isCurrentItem(item, index)
  if (props.variant === 'emphasized') {
    const base = [
      'inline-flex items-center min-w-0 max-w-[min(11rem,42vw)] sm:max-w-xs md:max-w-sm',
      'transition-[color,font-size,font-weight] duration-150 ease-out'
    ]
    if (current || !item.to) {
      // Current page: slightly larger; previous segments match old compact breadcrumb (text-xs)
      base.push('text-sm sm:text-base text-text font-semibold cursor-default')
    } else {
      base.push('text-xs text-text-secondary font-medium hover:text-text')
    }
    return base
  }
  return [
    'breadcrumb-item',
    current ? 'breadcrumb-item-current' : 'breadcrumb-item-link'
  ]
}
</script>

<style scoped>
.breadcrumb-item {
  display: inline-flex;
  align-items: center;
  transition: color 150ms ease-out;
}

.breadcrumb-item-link {
  color: var(--color-text-muted);
  font-weight: 500;
}

.breadcrumb-item-link:hover {
  color: var(--color-text);
}

.breadcrumb-item-current {
  color: var(--color-text);
  font-weight: 600;
}

.breadcrumb-separator {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
}

@media (min-width: 640px) {
  .breadcrumb-separator {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }
}

.breadcrumbs-container ol {
  min-width: 0;
}

.breadcrumbs-container .breadcrumb-item {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 640px) {
  .breadcrumbs-container .breadcrumb-item {
    max-width: none;
  }
}

@media (max-width: 640px) {
  .breadcrumbs-container ol {
    flex-wrap: wrap;
  }
}
</style>
